import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Truck, ArrowRight, Loader2, Smartphone, ShieldCheck, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { authApi, saveAuth } from '../lib/api';

export default function DriverLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState('phone'); // phone | otp
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [demoOtp, setDemoOtp] = useState('');
  const inputs = useRef([]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const sendOtp = async () => {
    const clean = phone.replace(/\s/g, '');
    if (!/^(\+91)?[6-9]\d{9}$/.test(clean)) {
      toast({ title: 'Invalid mobile', description: 'Enter a valid 10-digit Indian mobile number.' });
      return;
    }
    const finalPhone = clean.startsWith('+91') ? clean : `+91${clean}`;
    setLoading(true);
    try {
      const r = await authApi.sendOtp(finalPhone);
      setDemoOtp(r.demo_otp || '');
      setPhone(finalPhone);
      setStep('otp');
      setCountdown(30);
      toast({ title: 'OTP sent!', description: `SMS sent to ${finalPhone} (Demo OTP: ${r.demo_otp}) — MOCKED, no real SMS.` });
      setTimeout(() => inputs.current[0]?.focus(), 100);
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again shortly' });
    } finally { setLoading(false); }
  };

  const handleOtpChange = (idx, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...otp]; next[idx] = val; setOtp(next);
    if (val && idx < 5) inputs.current[idx + 1]?.focus();
    if (next.every(d => d)) verifyOtp(next.join(''));
  };

  const handleOtpKey = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) inputs.current[idx - 1]?.focus();
  };

  const verifyOtp = async (code) => {
    setLoading(true);
    try {
      const r = await authApi.verifyOtp({ phone, otp: code, name: '' });
      saveAuth(r.token, r.user);
      toast({ title: 'Welcome!', description: 'Signed into driver app.' });
      navigate('/driver');
    } catch (err) {
      toast({ title: 'Verification failed', description: err?.response?.data?.detail || 'Invalid OTP' });
      setOtp(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7FAF9] flex flex-col">
      <header className="p-4 flex items-center gap-2">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center">
            <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-xl text-[#0B2545] font-[Poppins]">EZLOAD Driver</span>
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {step === 'phone' ? (
            <>
              <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center mb-5">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-2xl font-extrabold text-[#0B2545] font-[Poppins]">Sign in with mobile</h1>
              <p className="text-sm text-gray-500 mt-1 mb-6">We'll send a 6-digit OTP to your phone.</p>

              <Label className="text-sm">Mobile Number</Label>
              <div className="flex mt-1.5">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-gray-50 text-sm text-gray-500">+91</span>
                <Input type="tel" maxLength={10} placeholder="98765 43210" className="h-12 rounded-l-none text-lg" value={phone.replace(/^\+91/, '')} onChange={e => setPhone(e.target.value)} />
              </div>

              <Button onClick={sendOtp} disabled={loading} className="w-full h-12 mt-5 bg-[#00A651] hover:bg-[#008a43] text-white">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <>Send OTP <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>

              <p className="text-[11px] text-gray-500 mt-4 text-center leading-relaxed">By continuing, you agree to EZLOAD's Terms & Privacy. Standard SMS rates may apply.</p>
              <div className="mt-6 pt-4 border-t border-gray-100 text-center">
                <p className="text-xs text-gray-500">Shipper or Fleet Owner? <Link to="/login" className="text-[#00A651] font-semibold">Sign in here</Link></p>
              </div>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-[#00A651]/10 flex items-center justify-center mb-5">
                <ShieldCheck className="w-7 h-7 text-[#00A651]" />
              </div>
              <h1 className="text-2xl font-extrabold text-[#0B2545] font-[Poppins]">Enter OTP</h1>
              <p className="text-sm text-gray-500 mt-1 mb-6">6-digit code sent to <span className="font-semibold text-[#0B2545]">{phone}</span></p>

              {demoOtp && (
                <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg p-3 mb-5">
                  <p className="text-[11px] font-semibold text-[#8B5E00]">DEMO MODE (SMS is MOCKED)</p>
                  <p className="text-xs text-[#8B5E00]">Your OTP: <span className="font-mono font-bold text-lg tracking-widest">{demoOtp}</span></p>
                  <p className="text-[10px] text-[#8B5E00]/80 mt-1">In production, add Twilio/MSG91 keys to send real SMS.</p>
                </div>
              )}

              <div className="flex justify-center gap-2">
                {otp.map((d, i) => (
                  <input key={i} ref={el => inputs.current[i] = el} value={d} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKey(i, e)} maxLength={1} inputMode="numeric" className="w-12 h-14 border border-gray-300 rounded-lg text-center text-2xl font-bold text-[#0B2545] focus:border-[#00A651] focus:ring-2 focus:ring-[#00A651]/20 outline-none" />
                ))}
              </div>

              <div className="mt-4 text-center text-sm">
                {countdown > 0 ? (
                  <span className="text-gray-500">Resend OTP in {countdown}s</span>
                ) : (
                  <button onClick={sendOtp} disabled={loading} className="text-[#00A651] font-semibold flex items-center gap-1 mx-auto">
                    <RefreshCw className="w-3 h-3" />Resend OTP
                  </button>
                )}
              </div>

              <Button onClick={() => verifyOtp(otp.join(''))} disabled={loading || otp.some(d => !d)} className="w-full h-12 mt-5 bg-[#00A651] hover:bg-[#008a43] text-white">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Sign in'}
              </Button>
              <button onClick={() => { setStep('phone'); setOtp(['','','','','','']); }} className="w-full mt-3 text-xs text-gray-500">Change mobile number</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
